import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { TranslatePipe } from '@ngx-translate/core';
import { TegelModule } from '@scania/tegel-angular-17';
import { AnnouncementService } from '@shared/components/announcement-banner/announcement.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'app-announcement-banner',
  imports: [TegelModule, TranslatePipe],
  templateUrl: './announcement-banner.component.html',
  styleUrl: './announcement-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AnnouncementBannerComponent {
  private readonly announcementService = inject(AnnouncementService);

  private readonly router = inject(Router);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly announcement = computed(() => {
    const url = this.currentUrl();
    const skippedRoute = url.startsWith('/map');

    if (skippedRoute) return null;

    return this.announcementService.activeAnnouncement();
  });

  protected readonly hasAction = computed(() => {
    const announcement = this.announcement();
    return Boolean(announcement?.linkTextKey?.trim() && announcement.url?.startsWith('https://'));
  });

  onLinkClick(): void {
    const announcement = this.announcement();
    if (!announcement) return;

    datadogRum.addAction('announcement-link-click', {
      announcementId: announcement.id,
      url: announcement.url,
    });
    this.onClose();
  }

  onClose(): void {
    const announcement = this.announcement();
    if (!announcement) return;
    this.announcementService.markSeen(announcement.id);
  }
}
